import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tabs, Tab } from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { GroupedTabs } from 'components/GroupedTabs';
import { AppRouteProps } from 'routes/AppRoute';
import { useAppRouteContext } from 'contexts/AppRouteContext';
import { useAuthorization } from 'hooks/useAuthorization';

import { PageTabsProps, PageTabProps } from './PageTabs.types';

export const PageTabs: React.FunctionComponent<PageTabsProps> = (props) => {
  const PageTab = Tab as React.ElementType<PageTabProps>;
  const { tabPanelId, tabProps, disabledTabIds, groupedTabs, ...other } = props;
  const { currentRoute, currentLevelRoutes } = useAppRouteContext();
  const { hasPermission } = useAuthorization();
  const routeParams: { [key: string]: string } = useParams();
  const { t } = useTranslation();
  const tabRoutes: AppRouteProps[] = currentLevelRoutes.filter((route) => {
    if (!route.hasTab) {
      return false;
    }

    if (
      route.authorization &&
      !route.authorization.every(({ permission, service }) => hasPermission(service, permission))
    ) {
      return false;
    }

    if (disabledTabIds && disabledTabIds.includes(route.path)) {
      return false;
    }

    return true;
  });
  let currentTab = 0;

  if (currentRoute) {
    currentTab = tabRoutes.indexOf(currentRoute);
    // Coerce -1 for not found to 0.
    if (currentTab === -1) {
      // If we're on a nested route, find the parent route.
      // This supports having multiple pages with the same tab.
      const parentRoute = tabRoutes
        .filter((route) => currentRoute.path.startsWith(route.path))
        .pop();
      currentTab = parentRoute ? tabRoutes.indexOf(parentRoute) : 0;
    }
  }

  // Our route config is full of :params. Interpolate what we can into links.

  function interpolateParams(value: string) {
    return Object.keys(routeParams).reduce(
      (result, param: string) =>
        result.replace(new RegExp(`/:${param}(/|$)`, 'g'), `/${routeParams[param]}$1`),
      value,
    );
  }

  // Use GroupedTabs when grouped mode is enabled and there are more than 6 tabs
  if (groupedTabs && tabRoutes.length > 6) {
    return (
      <GroupedTabs tabPanelId={tabPanelId} tabProps={tabProps} disabledTabIds={disabledTabIds} />
    );
  }

  return (
    <Tabs value={currentTab} variant="scrollable" data-testid="page-tabs" {...other}>
      {tabRoutes.map((route) => (
        <PageTab
          aria-controls={tabPanelId}
          component={Link}
          id={`tab-${route.path}`}
          key={`tab-${route.path}`}
          label={
            route.icon ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {route.icon}
                {t(route.name!)}
              </span>
            ) : (
              t(route.name!)
            )
          }
          to={interpolateParams(route.path)}
          {...tabProps}
        />
      ))}
    </Tabs>
  );
};

export default PageTabs;
